import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import FDCSidebar from "./navigation/FDCSidebar";
import FDCMobileNavigation from "./navigation/FDCMobileNavigation";
import FDCHeader from "./navigation/FDCHeader";
import FDCGeoWorkspace from "../../../geo-intelligence/fdc/FDCGeoWorkspace.jsx";
import FDCDashboard from "./dashboard/FDCDashboard";
import FDCClients from "./clients/FDCClients";
import FDCOnboardingSwitcher from "../shared-onboarding/FDCOnboardingSwitcher.jsx";
import FDCTargets from "./targets/FDCTargets";
import UniCloudManagement from "./unicloud/UniCloudManagement";
import FDCWallet from "./wallet/FDCWallet";
import FDCPerformance from "./performance/FDCPerformance";
import FDCTraining from "./training/FDCTraining";
import FDCQueries from "./queries/FDCQueries";
import FDCAdministration from "./administration/FDCAdministration";
import FDCProfileDocuments from "./profile/FDCProfileDocuments";
import FDCNotifications from "./notifications/FDCNotifications";
import FDCSettingsSecurity from "./settings/FDCSettingsSecurity";
import FDCActivity from "./pages/FDCActivity";
import FDCMessages from "./pages/FDCMessages";
import FDCSupport from "./pages/FDCSupport";
import { staffLogOut } from "../../../store/redux/universeStaffReducer.js";
import { configureStaffSocket, staffSocket } from "./services/fdcSocket";
import { fdcRequest } from "./services/fdcApi";
import useFDCLiveSession from "./services/useFDCLiveSession";
import { pageTitles } from "./navigation/fdcNavigation";

import { getFDCDashboard, getFDCProfile, updateFDCProfile } from "./services/fdcApi";
import "./FDCWorkspace.css";
import "./FDCWorkspace.rwd.css";

const validPages = new Set(Object.keys(pageTitles));

const resolveStaffId = (staffData) =>
  staffData?.staffId ||
  staffData?.employeeId ||
  staffData?.personalInfo?.staffId ||
  staffData?.employment?.staffId ||
  "";

const buildWorkspaceUser = (profile, fallback) => {
  if (!profile) return fallback;
  const personal = profile.personalInfo || {};
  const nameParts = String(profile.name || "").trim().split(/\s+/).filter(Boolean);
  const firstName = personal.firstName || nameParts[0] || fallback.firstName;
  const lastName = personal.lastName || personal.surname || nameParts.slice(1).join(" ") || fallback.lastName;
  return {
    ...fallback,
    firstName,
    lastName,
    initials: `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase(),
    role: profile.employment?.rank || fallback.role,
    level: profile.employment?.level || fallback.level,
    state: profile.assignment?.state || fallback.state,
    branch: profile.assignment?.branch || fallback.branch,
    status: profile.employment?.status || fallback.status,
    employeeId: profile.staffId || fallback.employeeId,
  };
};

export default function FDCWorkspace() {
  const dispatch=useDispatch(),redirect=useNavigate();
  const sessionToken=useSelector(state=>state.universeStaffs?.sessionToken);
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingFoundation, setLoadingFoundation] = useState(false);
  const [foundationError, setFoundationError] = useState("");

  // Existing application staff state is used only to identify the authenticated FDC.
  // Page/navigation state remains in URL search parameters as requested.
  const existingStaffData = useSelector(
    (state) => state.universeStaffs?.activeStaff
  );
  const staffId = resolveStaffId(existingStaffData);

  const rawPage = searchParams.get("page");
  const page = validPages.has(rawPage) ? rawPage : "dashboard";
  const title = pageTitles[page] || "Dashboard";
  const live=useFDCLiveSession(sessionToken,page,()=>{configureStaffSocket(null);dispatch(staffLogOut());redirect("/administration/staff-login",{replace:true});});

  useEffect(() => {
    if (!rawPage || !validPages.has(rawPage)) {
      const next = new URLSearchParams(searchParams);
      next.set("page", "dashboard");
      setSearchParams(next, { replace: true });
    }
  }, [rawPage, searchParams, setSearchParams]);

  const loadFoundation = useCallback(async () => {
    if (!staffId) {
      setFoundationError("No FDC staffId was found in the authenticated staff session.");
      return;
    }
    try {
      setLoadingFoundation(true);
      setFoundationError("");
      const [profileResponse, dashboardResponse] = await Promise.all([
        getFDCProfile(staffId),
        getFDCDashboard(staffId),
      ]);
      setProfile(profileResponse?.data || null);
      setDashboardData(dashboardResponse?.data || null);
    } catch (error) {
      setFoundationError(
        error?.response?.data?.message || error?.message || "Failed to load FDC account data."
      );
    } finally {
      setLoadingFoundation(false);
    }
  }, [staffId]);

  useEffect(() => {
    loadFoundation();
  }, [loadFoundation]);

  const navigateWorkspace = useCallback((nextPage, extra = {}, options = {}) => {
    const next = new URLSearchParams(options.preserve === false ? "" : searchParams);
    next.set("page", nextPage);
    next.delete("view");
    next.delete("id");
    if(nextPage!=="onboarding" || extra.target===undefined)next.delete("target");
    Object.entries(extra).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const workspaceUser = useMemo(
    () => buildWorkspaceUser(profile || existingStaffData,{firstName:"FDC",lastName:"",initials:"FDC",role:"Field Development Consultant",level:"",state:"",branch:"",status:"",employeeId:staffId}),
    [profile,existingStaffData,staffId]
  );

  useEffect(()=>{configureStaffSocket(sessionToken);let timer;const update=()=>{clearTimeout(timer);timer=setTimeout(loadFoundation,150)};staffSocket.on("staff:updated",update);staffSocket.on("staff:notification",update);staffSocket.on("connect",update);return()=>{clearTimeout(timer);["staff:updated","staff:notification","connect"].forEach(event=>staffSocket.off(event,update))}},[sessionToken,loadFoundation]);
  const logout=async()=>{try{await fdcRequest(staffId,"sessions/logout","post",{}, {timeout:5000})}catch{}finally{configureStaffSocket(null);dispatch(staffLogOut());redirect("/administration/staff-login",{replace:true})}};
  const saveProfile = useCallback(async (payload) => {
    if (!staffId) throw new Error("FDC staffId is unavailable.");
    const response = await updateFDCProfile(staffId, payload);
    setProfile(response?.data || null);
    return response;
  }, [staffId]);

  const content = useMemo(() => {
    if (page === "dashboard") return <FDCDashboard onNavigate={navigateWorkspace} apiData={dashboardData} loading={loadingFoundation} error={foundationError} onRetry={loadFoundation} />;
    if (page === "map") return <FDCGeoWorkspace onClose={() => navigateWorkspace("dashboard")} />;
    if (page === "clients") return <FDCClients onNavigate={navigateWorkspace} staffId={staffId} />;
    if (page === "onboarding") return <FDCOnboardingSwitcher onNavigate={navigateWorkspace} staffId={staffId} onRegistered={loadFoundation} />;
    if (page === "targets") return <FDCTargets onNavigate={navigateWorkspace} staffId={staffId} />;
    if (page === "unicloud") return <UniCloudManagement onNavigate={navigateWorkspace} staffId={staffId} />;
    if (page === "wallet") return <FDCWallet staffId={staffId} />;
    if (page === "performance") return <FDCPerformance staffId={staffId} />;
    if (page === "training") return <FDCTraining staffId={staffId} />;
    if (page === "queries") return <FDCQueries staffId={staffId} />;
    if (page === "administration") return <FDCAdministration staffId={staffId} />;
    if (page === "profile") return <FDCProfileDocuments staffId={staffId} staffData={profile || existingStaffData} onSaveProfile={saveProfile} loading={loadingFoundation} />;
    if (page === "notifications") return <FDCNotifications staffId={staffId} />;
    if (page === "settings") return <FDCSettingsSecurity staffId={staffId} />;
    if(page==="activity")return <FDCActivity staffId={staffId} onNavigate={navigateWorkspace}/>;
    if(page==="messages")return <FDCMessages staffId={staffId}/>;
    return <FDCSupport staffId={staffId}/>;
  }, [page, title, navigateWorkspace, dashboardData, loadingFoundation, foundationError, loadFoundation, staffId, profile, existingStaffData, saveProfile]);

  return (
    <section className="fdcWorkspace">
      <FDCSidebar page={page} onNavigate={navigateWorkspace} user={workspaceUser} onLogout={logout} unread={dashboardData?.notifications?.unread || 0} messagesUnread={dashboardData?.notifications?.messagesUnread || 0}/>
      <FDCMobileNavigation open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} page={page} onNavigate={navigateWorkspace} user={workspaceUser} unread={dashboardData?.notifications?.unread || 0} messagesUnread={dashboardData?.notifications?.messagesUnread || 0}/>
      <main className="fdcMain">
        <FDCHeader title={title} user={workspaceUser} onMenu={() => setMobileNavOpen(true)} onNavigate={navigateWorkspace} unread={dashboardData?.notifications?.unread || 0} onSearch={query=>navigateWorkspace("clients",{q:query})}/>
        <div className="fdcContent">
          {!live.connected&&<div className="fdcCard" role="status">Live updates are disconnected. <button type="button" onClick={live.reconnect}>Reconnect</button></div>}
          {live.error&&<p role="alert">{live.error}</p>}
          {live.request&&<div className="fdcCard" role="region" aria-label="Location request"><strong>{live.sharing?"Live location sharing is active":"Location request from "+live.request.requesterName}</strong><p>{live.request.reason}</p><p>Sharing ends at {new Date(live.request.expiresAt).toLocaleTimeString()}. Your browser will ask for location permission.</p>{live.sharing?<button type="button" onClick={live.stop}>Stop sharing</button>:<><button type="button" onClick={live.accept}>Allow location sharing</button><button type="button" onClick={live.decline}>Decline</button></>}</div>}
          {content}</div>
      </main>
    </section>
  );
}
